import React, { useEffect, useState } from "react";
import { Toaster } from "react-hot-toast";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import SignUp from "./pages/SignUp";
import { Route, Routes } from "react-router-dom";
import Login from "./pages/Login";
import ChangePassword from "./pages/ChangePassword";
import ResetPassword from "./pages/ResetPassword";
import CreatePassword from "./pages/CreatePassword";
import LoginOtp from "./pages/LoginOtp";
import SignUpOtp from "./pages/SignUpOtp";
import Sidebar from "./components/Sidebar";
import Dashboard from "./pages/Dashboard";
import Transactions from "./pages/Reports/Transactions";
import Audits from "./pages/Reports/Audits";
import ConfigurationNav from "./components/ConfigurationNav";
import CardScheme from "./pages/Configurations/CardScheme";
import Gateway from "./pages/Configurations/Gateway";
import UserRoles from "./pages/Configurations/UserRoles";
import Permission from "./pages/Configurations/Permission";
import Prospects from "./pages/Prospects";
import Users from "./pages/Users/Users";
import ReportNav from "./components/ReportNav";
import Merchant from "./pages/Merchant/Merchant";
import MerchantInfo from "./pages/Merchant/MerchantInfo";
import PendingMerchantDetails from "./pages/Merchant/PendingMerchantDetails";
import PrivateRoute from "./components/PrivateRoute";
import { useAuth } from "./context/AuthContextProvider";
import CompleteInvitation from "./pages/CompleteInvitation";
import CompletePasswordReset from "./pages/CompletePasswordReset";
import SettingsNav from "./components/SettingsNav";
import Profile from "./pages/Settings/Profile";
import Password from "./pages/Settings/Password";
import FourthLevelProspects from "./pages/FourthLevelProspects";
import Review from "./pages/Review";
import ConfigRequests from "./pages/ConfigRequests";
import ClientTransactions from "./pages/ClientTransactions";
import CreateClient from "./pages/CreateClient";
import EditMerchantRequests from "./pages/EditMerchantRequests";
import Organisations from "./pages/Organisation/Organisations";
import OrganisationRequests from "./pages/Organisation/OrganisationRequests";
import InactiveRequests from "./pages/Organisation/InactiveRequests";
import RefundRequests from "./pages/Organisation/RefundRequests";
import SubscriptionPage from "./pages/Organisation/Subscription";
import SplitPayment from "./pages/SplitPayment";
import RolesPermissions from "./pages/Organisation/RolesPermissions";
import ApproveMsc from "./pages/Merchant/ApproveMsc";
import CheckMail from "./pages/CheckMail";
import ConfirmEmail from "./pages/ConfirmEmail";

const queryClient = new QueryClient();

function App() {
  const [inactive, setInactive] = useState(false);
  const { logout } = useAuth();

  // Timeout duration for inactivity (5 minutes in milliseconds)
  const INACTIVE_TIMEOUT = 300000;
  let inactivityTimer;

  // Function to handle user inactivity
  const handleInactive = () => {
    setInactive(true);
    logout();
  };

  // Function to reset the inactivity timer
  const resetTimer = () => {
    clearTimeout(inactivityTimer);
    if (!inactive) {
      inactivityTimer = setTimeout(handleInactive, INACTIVE_TIMEOUT);
    }
  };

  // Function to logout and clear storage

  useEffect(() => {
    // Initial setup of the inactivity timer
    inactivityTimer = setTimeout(handleInactive, INACTIVE_TIMEOUT);

    // Event listeners to detect user activity
    const events = ["mousemove", "keydown", "mousedown", "touchstart"];
    const resetEvents = () => {
      if (inactive) {
        setInactive(false);
      }
      resetTimer();
    };
    events.forEach((event) => {
      window.addEventListener(event, resetEvents);
    });

    // Cleanup function to remove event listeners and clear the timer
    return () => {
      clearTimeout(inactivityTimer);
      events.forEach((event) => {
        window.removeEventListener(event, resetEvents);
      });
    };
  }, [inactive]);

  return (
    <>
      <QueryClientProvider client={queryClient}>
        <Toaster position="top-center" />
        <Routes>
          <Route path="/sign-up" element={<SignUp />} />
          <Route path="/" element={<Login />} />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/create-password" element={<CreatePassword />} />
          <Route path="/login-otp" element={<LoginOtp />} />
          <Route path="/signup-otp" element={<SignUpOtp />} />
          <Route
            path="/complete-registration/:email"
            element={<CompleteInvitation />}
          />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/check-email" element={<CheckMail />} />
          <Route path="/resetPassword" element={<CompletePasswordReset />} />
          <Route element={<PrivateRoute />}>
            <Route path="/*" element={<Sidebar />}>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="users" element={<Users />} />
              <Route path="config-requests" element={<ConfigRequests />} />
              <Route path="organisation" element={<Organisations />} />
              <Route path="roles-permissions" element={<RolesPermissions />} />
              <Route path="approve-msc" element={<ApproveMsc />} />
              <Route path="split-payment" element={<SplitPayment />} />
              <Route
                path="domain-requests"
                element={<OrganisationRequests />}
              />
              <Route path="inactive-requests" element={<InactiveRequests />} />
              <Route path="refund-requests" element={<RefundRequests />} />
              <Route path="subscriptions" element={<SubscriptionPage />} />
              <Route path="create-client" element={<CreateClient />} />
              <Route
                path="client-transactions"
                element={<ClientTransactions />}
              />
              <Route
                path="edit-merchant-requests"
                element={<EditMerchantRequests />}
              />
              <Route path="merchants">
                <Route index element={<Merchant />} />
                <Route path=":id" element={<MerchantInfo />} />
                <Route path="pending/:id" element={<PendingMerchantDetails />} />
              </Route>

              <Route path="prospects">
                <Route index element={<Prospects />} />
                <Route path=":id" element={<Review />} />
              </Route>

              <Route path="level-four-prospects">
                <Route index element={<FourthLevelProspects />} />
                <Route path=":id" element={<Review />} />
              </Route>
            </Route>
            <Route path="reports" element={<ReportNav />}>
              <Route path="" element={<Transactions />} />
              <Route path="audits" element={<Audits />} />
            </Route>
            <Route path="configurations" element={<ConfigurationNav />}>
              <Route path="" element={<CardScheme />} />
              <Route path="gateway" element={<Gateway />} />
              <Route path="user-roles" element={<UserRoles />} />
              <Route path="permission" element={<Permission />} />
            </Route>

            <Route path="settings" element={<SettingsNav />}>
              <Route path="" element={<Profile />}>
                <Route path="profile">
                  <Route path="" element={<Password />} />
                </Route>
              </Route>
            </Route>
          </Route>
        </Routes>
      </QueryClientProvider>
    </>
  );
}

export default App;
